import React, {
  createContext,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import moment from "moment-timezone";
import styled, { css } from "styled-components";

import { Button } from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import { Chip } from "../../components/Chip";
import { DatePicker, TimestampDateRange } from "../../components/DatePicker";
import { Dot } from "../../components/Dot";
import {
  CalendarIcon,
  ClockAddIcon,
  CollapseExpandSingleIcon,
} from "../../components/Icons";
import { InputHours } from "../../components/Input";
import { Label } from "../../components/Label";
import { Modal } from "../../components/Modal";
import { ProgressPie } from "../../components/ProgressPie";
import { Select } from "../../components/Select";
import { SelectTrigger } from "../../components/SelectTrigger";
import { Textarea } from "../../components/Textarea";
import { SlideFromTop } from "../../components/Transitions";
import {
  Body2,
  Caption1,
  Caption2,
  Header3,
} from "../../components/Typography";
import { formatHours } from "../../utils";

/**
 * Interactive list-view column cells for the `Presentation/List view` and
 * `Presentation/List View ID Column` stories — the Label, Date and Time cells
 * with their editors, mirroring
 * `new-activecollab-front/src/components/ListView/columns/*`, plus an
 * Identifier cell that only the ID-column story uses (it has no counterpart in
 * the app yet):
 *
 * - **Identifier** (no app equivalent): `#<id>` as a Copy Link trigger, styled
 *   like the Date trigger. One typography at every length — the column's width
 *   is what adapts to how large the identifiers get.
 * - **Label** (`columns/Label.js` + `LabelTrigger`/`LabelItems`): chips for 1–2
 *   labels, dots from 3 up, `+N` past 8, and a DS `Select` picker (search,
 *   colored dot + label name in the label's own colour, checkbox, "+ Add new").
 * - **Date** (`columns/Date.js`): a range trigger that formats like the app
 *   (`formatDateRange`), turns red when overdue, and opens the DS `DatePicker`
 *   in atomic mode so it gets the Save / ✕ / Clear footer.
 * - **Time** (`columns/Time.js`): `ProgressPie` + `tracked/estimated`, coloured
 *   by how much of the estimate is used, opening the "Add Time Record" dialog.
 *
 * Everything is local state — there is no store, so edits live as long as the
 * story is mounted.
 */

const SANS =
  '-apple-system, BlinkMacSystemFont, "Inter", "Roboto", "Helvetica Neue", Arial, sans-serif';

/**
 * CSS-only tooltip, mirroring the app's `.c-cooltip.c-cooltip--top` (see
 * `styles/c_cooltip.less`). The list renders hundreds of these, and the DS
 * `Tooltip` mounts a Portal + Popper per instance — far too heavy per cell — so
 * the cells use `data-tooltip` exactly like the real list view does.
 */
const CooltipStyle = css`
  &[data-tooltip],
  [data-tooltip] {
    position: relative;
  }

  &[data-tooltip]::after,
  [data-tooltip]::after {
    pointer-events: none;
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 0.5rem);
    left: 50%;
    transform: scale(0.85) translateX(-50%);
    transform-origin: 0;
    opacity: 0;
    max-width: 12.5rem;
    padding: 0.25rem 0.625rem;
    border-radius: 0.5rem;
    background-color: rgba(0, 0, 0, 0.9);
    color: #fff;
    font-family: ${SANS};
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.3;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (hover: hover) {
    &[data-tooltip]:not(:focus):hover::after,
    [data-tooltip]:not(:focus):hover::after {
      transition: opacity 0.15s ease, transform 0.15s ease;
      transition-delay: 0.6s;
      transform: scale(1) translateX(-50%);
      opacity: 1;
    }
  }
`;

/* ================================================================== */
/* Shared trigger button (mirrors ListView/ListButton.js)              */
/* ================================================================== */

interface ListButtonProps {
  /** injected by DS `Menu` when it clones the target */
  open?: boolean;
  className?: string;
  children?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  "aria-label"?: string;
}

/**
 * `Menu` clones its `target` and injects `open`, so — like the app's
 * `ListButton` — the trigger has to swallow that prop and turn it into the DS
 * `active` state plus an `.open` class the row CSS uses to keep the trigger
 * visible while its menu is open.
 */
const ListButtonBase = forwardRef<HTMLButtonElement, ListButtonProps>(
  ({ open, className, children, ...rest }, ref) => (
    <Button
      {...rest}
      ref={ref}
      type="button"
      variant="text gray"
      active={open}
      className={`list-button${className ? ` ${className}` : ""}${
        open ? " open" : ""
      }`}
    >
      {children}
    </Button>
  )
);

ListButtonBase.displayName = "ListButtonBase";

/*
 * The DS Button treats a single element child as "icon only" — inline-flex,
 * centered, a fixed 32px wide and `overflow: hidden`, which squashes a date or
 * a chip. `&&` doubles the specificity so these rules win over that block; the
 * button then hugs its content and the column's `justify-content: flex-end`
 * parks it against the right edge.
 */
const StyledListButton = styled(ListButtonBase)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    width: auto;
    min-width: 0;
    max-width: 100%;
    padding: 4px 8px;
    overflow: visible;
    text-align: right;
  }

  ${CooltipStyle}
`;

/* ================================================================== */
/* Identifier (task ID)                                               */
/* ================================================================== */

const StyledIdValue = styled.span`
  font-family: ${SANS};
  white-space: nowrap;
  /* equal-width digits so a column of ids reads as a column, not a ragged list */
  font-variant-numeric: tabular-nums;
`;

interface IdTriggerProps extends ListButtonProps {
  value: string;
  tooltip: string;
}

/**
 * Same trigger the Date cell uses, so the id gets the identical hover outline
 * and 8px inset — it is a link, not an editor, so it is always visible
 * (`has-value`) rather than appearing on row hover.
 *
 * The typography is Caption 1 bold at every identifier length, like the Date
 * value next to it; long identifiers are handled by the column's width tier,
 * not by shrinking the text.
 */
const IdTrigger = forwardRef<HTMLButtonElement, IdTriggerProps>(
  ({ value, tooltip, ...rest }, ref) => (
    <StyledListButton
      {...rest}
      ref={ref}
      className="show-on-hover has-value"
      aria-label={`Copy link to task ${value}`}
      data-tooltip={tooltip}
    >
      <StyledIdValue>
        <Caption1 weight="bold" color="secondary">
          {value}
        </Caption1>
      </StyledIdValue>
    </StyledListButton>
  )
);

IdTrigger.displayName = "IdTrigger";

/**
 * Task identifier cell — the `#<id>` value as a copy-link control, mirroring
 * how `tasks/Metadata.js` turns `Task #248` into the task's Copy Link action:
 * the click writes the task URL to the clipboard and reports back so the page
 * can raise the app's "Link copied to clipboard." toast (utils/copyLinkToClipboard).
 * It never navigates — the row itself is the navigation target.
 */
export const IdentifierCell = ({
  identifier,
  link,
  onCopy,
}: {
  identifier: number;
  /** absolute task URL — what lands on the clipboard */
  link: string;
  /** raised after the copy attempt so the page can show a toast */
  onCopy?: (copied: boolean) => void;
}): ReactElement => {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      /* the whole row navigates to the task, so copying must not also open it */
      event.preventDefault();
      event.stopPropagation();

      if (navigator.clipboard?.writeText) {
        navigator.clipboard
          .writeText(link)
          .then(() => onCopy?.(true))
          .catch(() => onCopy?.(false));
        return;
      }
      onCopy?.(false);
    },
    [link, onCopy]
  );

  return (
    <div className="col col-identifier">
      <IdTrigger
        value={`#${identifier}`}
        tooltip="Copy Link"
        onClick={handleClick}
      />
    </div>
  );
};

/* ================================================================== */
/* Labels                                                             */
/* ================================================================== */

export interface TaskLabel {
  id: string;
  name: string;
  /** chip background + dot colour (label colours are data, not theme) */
  color: string;
  /** the label's own text colour, used in the picker list */
  textColor: string;
}

/* The project's label catalogue. Names are uppercase, like in the app. */
const INITIAL_LABELS: TaskLabel[] = [
  {
    id: "in-progress",
    name: "IN PROGRESS",
    color: "#CDE8F5",
    textColor: "#3D7F9E",
  },
  {
    id: "needs-review",
    name: "NEEDS REVIEW",
    color: "#F9E79F",
    textColor: "#A98A17",
  },
  { id: "approved", name: "APPROVED", color: "#A9DFBF", textColor: "#3D9A6A" },
  { id: "blocked", name: "BLOCKED", color: "#F1948A", textColor: "#B5544C" },
  { id: "on-hold", name: "ON HOLD", color: "#F5CBA7", textColor: "#B4713A" },
  { id: "idea", name: "IDEA", color: "#FAD7A0", textColor: "#B98A2B" },
  { id: "todo", name: "TODO", color: "#D6EAF8", textColor: "#4A87B5" },
  { id: "research", name: "RESEARCH", color: "#D7BDE2", textColor: "#8155A0" },
  {
    id: "accessibility",
    name: "ACCESSIBILITY",
    color: "#AED6F1",
    textColor: "#3E7CA6",
  },
  { id: "copy", name: "COPY", color: "#F5B7B1", textColor: "#B5544C" },
  {
    id: "design-debt",
    name: "DESIGN DEBT",
    color: "#E8DAEF",
    textColor: "#7D5A99",
  },
  { id: "qa", name: "QA", color: "#A3E4D7", textColor: "#34907C" },
  {
    id: "client-input",
    name: "CLIENT INPUT",
    color: "#FDEBD0",
    textColor: "#B08640",
  },
  { id: "mobile", name: "MOBILE", color: "#D1F2EB", textColor: "#2E8B7A" },
  {
    id: "performance",
    name: "PERFORMANCE",
    color: "#FCF3CF",
    textColor: "#A38B1E",
  },
  { id: "asset", name: "ASSET", color: "#D5D8DC", textColor: "#6B7378" },
];

/* Colours handed out to labels created from the picker's "+ Add new" row. */
const NEW_LABEL_COLORS = [
  { color: "#F9E0F2", textColor: "#A4569B" },
  { color: "#DDEBFB", textColor: "#4E7BA8" },
  { color: "#E4F6D8", textColor: "#5F9440" },
  { color: "#FDE2D5", textColor: "#B36A45" },
];

interface LabelsContextValue {
  labels: TaskLabel[];
  labelsById: Record<string, TaskLabel>;
  addLabel: (name: string) => string;
}

const LabelsContext = createContext<LabelsContextValue>({
  labels: INITIAL_LABELS,
  labelsById: {},
  addLabel: () => "",
});

/**
 * Holds the label catalogue for the whole list, so a label created in one
 * task's picker shows up in every other picker too.
 */
export const LabelsProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const [labels, setLabels] = useState<TaskLabel[]>(INITIAL_LABELS);

  const addLabel = useCallback((name: string): string => {
    const trimmed = name.trim().toUpperCase();
    const id = `label-${trimmed.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    setLabels((prev) => {
      if (prev.some((label) => label.id === id)) {
        return prev;
      }
      const palette = NEW_LABEL_COLORS[prev.length % NEW_LABEL_COLORS.length];
      return [...prev, { id, name: trimmed, ...palette }];
    });
    return id;
  }, []);

  const value = useMemo(
    () => ({
      labels,
      labelsById: labels.reduce<Record<string, TaskLabel>>(
        (acc, label) => ({ ...acc, [label.id]: label }),
        {}
      ),
      addLabel,
    }),
    [addLabel, labels]
  );

  return (
    <LabelsContext.Provider value={value}>{children}</LabelsContext.Provider>
  );
};

const useTaskLabels = (): LabelsContextValue => useContext(LabelsContext);

/* Empty state pill (mirrors EntityLabel + .entity-label in list-view.less) */
const StyledLabelPlaceholder = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border: 1px solid var(--color-theme-700);
  border-radius: 4px;

  .list-button.open &,
  .list-button:hover & {
    border-color: var(--color-primary-700, var(--color-primary));
  }
`;

const StyledLabelItems = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;

  /* 2px gap between chips/dots (mirrors tw-mr-0.5 last:tw-mr-0) */
  > *:not(:last-child) {
    margin-right: 2px;
  }

  .rest-count {
    margin-left: 2px;
  }

  ${CooltipStyle}
`;

const MAX_SHOWN_LABELS = 8;

const LabelItems = ({ labels }: { labels: TaskLabel[] }): ReactElement => {
  const showAsDot = labels.length > 2;
  let shown = labels;
  let restCount = 0;

  if (labels.length > MAX_SHOWN_LABELS) {
    restCount = Math.min(labels.length - MAX_SHOWN_LABELS, 99);
    shown = labels.slice(0, MAX_SHOWN_LABELS - 1);
  }

  return (
    <StyledLabelItems>
      {shown.map((label) =>
        showAsDot ? (
          <Dot
            key={label.id}
            color={label.color}
            size={20}
            data-tooltip={label.name}
          />
        ) : (
          <Chip
            key={label.id}
            label={label.name}
            backgroundColor={label.color}
            color="#303037"
            style={{ maxWidth: "90px" }}
            typographyProps={{ variant: "Caption 2", weight: "bold" }}
            data-tooltip={label.name}
          />
        )
      )}
      {restCount > 0 ? (
        <Body2 weight="bold" className="rest-count">
          +{restCount}
        </Body2>
      ) : null}
    </StyledLabelItems>
  );
};

const LabelTrigger = forwardRef<
  HTMLButtonElement,
  ListButtonProps & { labels: TaskLabel[] }
>(({ labels, ...rest }, ref) => {
  if (labels.length === 0) {
    return (
      <StyledListButton
        {...rest}
        ref={ref}
        className="show-on-hover"
        aria-label="Add labels"
        data-tooltip="Add Labels"
      >
        <StyledLabelPlaceholder>
          <Caption2 weight="bold" color="tertiary">
            LABEL
          </Caption2>
        </StyledLabelPlaceholder>
      </StyledListButton>
    );
  }

  return (
    <StyledListButton {...rest} ref={ref} aria-label="Change labels">
      <LabelItems labels={labels} />
    </StyledListButton>
  );
});

LabelTrigger.displayName = "LabelTrigger";

export const LabelCell = ({
  labelIds,
  completed = false,
}: {
  labelIds?: string[];
  completed?: boolean;
}): ReactElement => {
  const { labels: catalog, labelsById, addLabel } = useTaskLabels();
  const [selected, setSelected] = useState<string[]>(labelIds ?? []);

  /* alphabetical, like LabelTrigger sorts before rendering */
  const selectedLabels = useMemo(
    () =>
      selected
        .map((id) => labelsById[id])
        .filter((label): label is TaskLabel => Boolean(label))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [labelsById, selected]
  );

  const options = useMemo(
    () =>
      catalog.map((label) => ({
        id: label.id,
        name: label.name,
        color: label.color,
        textColor: label.textColor,
      })),
    [catalog]
  );

  const handleChange = useCallback(
    (value: (string | number)[] | string | number) => {
      setSelected(
        Array.isArray(value)
          ? value.map(String)
          : [String(value)].filter(Boolean)
      );
    },
    []
  );

  const handleAddNew = useCallback(
    (name?: string) => {
      if (!name || !name.trim()) {
        return;
      }
      const id = addLabel(name);
      setSelected((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [addLabel]
  );

  /* Completed tasks show their labels read-only (mirrors Label.js) */
  if (completed) {
    return (
      <StyledReadOnlyCell className="col col-label">
        {selectedLabels.length > 0 ? (
          <LabelItems labels={selectedLabels} />
        ) : null}
      </StyledReadOnlyCell>
    );
  }

  return (
    <div className="col col-label">
      <Select
        type="multiple"
        options={options}
        selected={selected}
        onChange={handleChange}
        emptyValue="Add new"
        emptyAction={handleAddNew}
        autoHeightMax={278}
        position="bottom-end"
        target={<LabelTrigger labels={selectedLabels} />}
      />
    </div>
  );
};

/* ================================================================== */
/* Date                                                               */
/* ================================================================== */

/** The personalized date format the app defaults to (`MMM D. YYYY`). */
const DATE_FORMAT = "MMM D. YYYY";

/** Midnight UTC today — the reference point for the mock data and overdue. */
const todayUnix = (): number => moment.utc().startOf("day").unix();

/** Build a mock timestamp N days from today (negative = in the past). */
export const dayOffset = (offset: number): number =>
  moment.utc().startOf("day").add(offset, "days").unix();

/** mirrors utils/date.js → formatDateRange (month-first format) */
export const formatDateRange = (
  from?: number,
  to?: number
): string | undefined => {
  if (from === undefined || from === null) {
    return undefined;
  }
  const start = moment.unix(from).utc();
  const due = moment.unix(to ?? from).utc();
  const withoutYear = DATE_FORMAT.replace(/YYYY|,$/gi, "").trim();
  const withoutYearAndMonth = withoutYear.replace(/MMM|,$/gi, "").trim();

  if (from === to || to === undefined) {
    return start.format(withoutYear);
  }
  if (start.year() === due.year() && start.month() === due.month()) {
    return `${start.format(withoutYear)} - ${due.format(withoutYearAndMonth)}`;
  }
  return `${start.format(withoutYear)} - ${due.format(withoutYear)}`;
};

/** mirrors utils/date.js → formatDateSmart(…, long) */
const formatDateLong = (value?: number): string =>
  value ? moment.unix(value).utc().format(DATE_FORMAT) : "";

export const isOverdue = (dueOn?: number, completed?: boolean): boolean =>
  Boolean(dueOn && !completed && dueOn < todayUnix());

const StyledDateValue = styled.span`
  font-family: ${SANS};
  white-space: nowrap;
`;

/* completed rows render values without a trigger, so the cell itself hosts the
   CSS tooltip */
const StyledReadOnlyCell = styled.div`
  padding-right: 8px;

  ${CooltipStyle}
`;

/**
 * `DatePicker` wraps itself in a `width: 100%` inline-block, which would stretch
 * across the column and left-align the trigger the moment the picker mounts —
 * the value would visibly jump and the popper would anchor to the wrong spot.
 * Keeping that wrapper right-aligned makes the open state geometrically
 * identical to the closed one.
 */
const StyledPickerCell = styled.div`
  /* StyledDatePickerWrapper — DatePicker's unclassed outer div */
  > div {
    text-align: right;
  }
`;

interface DateTriggerProps extends ListButtonProps {
  value?: string;
  tooltip: string;
  overdue?: boolean;
}

const DateTrigger = forwardRef<HTMLButtonElement, DateTriggerProps>(
  ({ value, tooltip, overdue, ...rest }, ref) => (
    <StyledListButton
      {...rest}
      ref={ref}
      className={value ? "show-on-hover has-value" : "show-on-hover"}
      aria-label={value ? "Change dates" : "Add date"}
      data-tooltip={tooltip}
    >
      {value ? (
        <StyledDateValue>
          <Caption1 weight="bold" color={overdue ? "alert" : "secondary"}>
            {value}
          </Caption1>
        </StyledDateValue>
      ) : (
        <CalendarIcon />
      )}
    </StyledListButton>
  )
);

DateTrigger.displayName = "DateTrigger";

export const DateCell = ({
  startOn,
  dueOn,
  completed = false,
}: {
  startOn?: number;
  dueOn?: number;
  completed?: boolean;
}): ReactElement => {
  const [dates, setDates] = useState<{ from?: number; to?: number }>({
    from: startOn,
    to: dueOn,
  });
  /**
   * The DS `DatePicker` does real work on mount (timezone guess, month grids,
   * key handlers) — with one per row that is the single most expensive thing on
   * this page. So the closed state is just the trigger; the picker is mounted
   * on first click, already open, and unmounted again when it closes.
   */
  const [pickerMounted, setPickerMounted] = useState(false);

  /* moment isn't cheap either; only reformat when the dates actually change */
  const { value, overdue, tooltip } = useMemo(() => {
    const formatted = formatDateRange(dates.from, dates.to);
    let hint = "Add Date";
    if (dates.from) {
      hint =
        !dates.to || dates.to === dates.from
          ? formatDateLong(dates.from)
          : `${formatDateLong(dates.from)} - ${formatDateLong(dates.to)}`;
    }
    return {
      value: formatted,
      overdue: isOverdue(dates.to ?? dates.from, completed),
      tooltip: hint,
    };
  }, [completed, dates.from, dates.to]);

  const handleSave = useCallback((range?: TimestampDateRange) => {
    if (!range || range.from === null || range.from === undefined) {
      setDates({});
      return;
    }
    setDates({
      from: Number(range.from),
      to: range.to ? Number(range.to) : Number(range.from),
    });
  }, []);

  const handleClear = useCallback(() => setDates({}), []);

  const handleCalendarToggle = useCallback((next: boolean) => {
    if (!next) {
      setPickerMounted(false);
    }
  }, []);

  /* Completed tasks show the date read-only (mirrors Date.js) */
  if (completed) {
    return (
      <StyledReadOnlyCell className="col col-date">
        {value ? (
          <StyledDateValue data-tooltip={tooltip}>
            <Caption1 weight="bold" color={overdue ? "alert" : "secondary"}>
              {value}
            </Caption1>
          </StyledDateValue>
        ) : null}
      </StyledReadOnlyCell>
    );
  }

  if (!pickerMounted) {
    return (
      <div className="col col-date">
        <DateTrigger
          value={value}
          tooltip={tooltip}
          overdue={overdue}
          onClick={() => setPickerMounted(true)}
        />
      </div>
    );
  }

  return (
    <StyledPickerCell className="col col-date">
      <DatePicker
        open
        mode="custom"
        instant={false}
        maxRange={366}
        firstDayOfWeek={1}
        position="bottom-end"
        selected={{ from: dates.from ?? null, to: dates.to ?? null }}
        onSave={handleSave}
        onClear={handleClear}
        onCalendarToggle={handleCalendarToggle}
        target={
          <DateTrigger value={value} tooltip={tooltip} overdue={overdue} />
        }
      />
    </StyledPickerCell>
  );
};

/* ================================================================== */
/* Time                                                              */
/* ================================================================== */

/** mirrors utils/utils.jsx → calculateTimeToEstimatePercentage */
const timeToEstimatePercentage = (
  tracked: number,
  estimated: number
): number | null => {
  if (estimated > 0) {
    return tracked > 0 ? Math.round((tracked / estimated) * 100) : 0;
  }
  return null;
};

/** mirrors utils/utils.jsx → formatTrackedAndEstimatedTime */
const formatTrackedAndEstimated = (
  tracked: number,
  estimated: number
): string =>
  estimated > 0
    ? `${formatHours(tracked, false, false, "short")}/${formatHours(
        estimated,
        false,
        false,
        "short"
      )}`
    : formatHours(tracked, false, false, "short");

/* mirrors Time.js → progressColor() */
const progressColor = (percent: number): string => {
  if (percent >= 100) {
    return "var(--red-alert)";
  }
  if (percent >= 75) {
    return "var(--warning)";
  }
  return "var(--color-sucess-green)";
};

const StyledTimeValue = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: ${SANS};
  white-space: nowrap;

  .c-progress-pie,
  svg {
    flex-shrink: 0;
  }

  .time-pie {
    margin-right: 8px;
  }
`;

const TimeValue = ({
  tracked,
  estimated,
}: {
  tracked: number;
  estimated: number;
}): ReactElement => {
  const percent = timeToEstimatePercentage(tracked, estimated);

  return (
    <StyledTimeValue>
      {estimated > 0 ? (
        <ProgressPie
          className="time-pie"
          radius={10}
          progress={percent ?? 0}
          progressColor={progressColor(percent ?? 0)}
          backgroundColor="var(--color-theme-300)"
        />
      ) : null}
      <Caption1 weight="bold">
        {formatTrackedAndEstimated(tracked, estimated)}
      </Caption1>
    </StyledTimeValue>
  );
};

const TimeTrigger = ({
  tracked,
  estimated,
  onClick,
}: {
  tracked: number;
  estimated: number;
  onClick: () => void;
}): ReactElement => {
  const hasTime = tracked > 0 || estimated > 0;

  return (
    <StyledListButton
      onClick={onClick}
      className={hasTime ? "show-on-hover open" : "show-on-hover"}
      aria-label={hasTime ? "Edit time" : "Add time"}
      data-tooltip={hasTime ? "Edit Time" : "Add Time"}
    >
      {hasTime ? (
        <TimeValue tracked={tracked} estimated={estimated} />
      ) : (
        <ClockAddIcon />
      )}
    </StyledListButton>
  );
};

export interface TimeRecord {
  id: number;
  date: number;
  userId: string;
  userName: string;
  jobType: string;
  hours: number;
  billable: boolean;
  description?: string;
}

export interface TimeCellUser {
  id: string;
  name: string;
}

export const TimeCell = ({
  taskName,
  tracked = 0,
  estimated = 0,
  users,
  currentUserId,
  completed = false,
}: {
  taskName: string;
  tracked?: number;
  estimated?: number;
  users: TimeCellUser[];
  currentUserId: string;
  completed?: boolean;
}): ReactElement => {
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState<TimeRecord[]>(() =>
    tracked > 0
      ? [
          {
            id: 1,
            date: dayOffset(-3),
            userId: currentUserId,
            userName:
              users.find((user) => user.id === currentUserId)?.name ?? "You",
            jobType: "Design",
            hours: tracked,
            billable: true,
          },
        ]
      : []
  );

  const total = useMemo(
    () => records.reduce((sum, record) => sum + record.hours, 0),
    [records]
  );

  const handleAdd = useCallback((record: Omit<TimeRecord, "id">) => {
    setRecords((prev) => [{ ...record, id: prev.length + 1 }, ...prev]);
    setOpen(false);
  }, []);

  /* Completed tasks show the value without the "add" affordance (mirrors
     Time.js passing hideIcon for completed tasks) */
  if (completed) {
    return (
      <StyledReadOnlyCell className="col col-time">
        {total > 0 || estimated > 0 ? (
          <TimeValue tracked={total} estimated={estimated} />
        ) : null}
      </StyledReadOnlyCell>
    );
  }

  return (
    <div className="col col-time">
      <TimeTrigger
        tracked={total}
        estimated={estimated}
        onClick={() => setOpen(true)}
      />
      {open ? (
        <TimeRecordDialog
          open={open}
          taskName={taskName}
          users={users}
          currentUserId={currentUserId}
          records={records}
          total={total}
          onSubmit={handleAdd}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Add Time Record dialog (mirrors TimeRecordForm/*)                   */
/* ------------------------------------------------------------------ */

const JOB_TYPES = [
  { id: "design", name: "Design" },
  { id: "development", name: "Development" },
  { id: "devops", name: "Devops" },
  { id: "consulting", name: "Consulting" },
  { id: "qa", name: "QA" },
];

/* The dialog paper — the app builds this one by hand rather than with DS
   `Dialog` because it is 770px wide (`.time_record_form_modal_wrapper`). */
const StyledTimeRecordPaper = styled.div`
  position: relative;
  width: 770px;
  max-width: 95vw;
  max-height: calc(100vh - 156px);
  margin: 126px auto 30px auto;
  padding: 20px 30px;
  overflow: auto;
  border-radius: 8px;
  background-color: var(--page-paper-main);
  color: var(--color-theme-900);
  box-shadow: var(--shadow-primary);
  box-sizing: border-box;
  font-family: ${SANS};

  * {
    box-sizing: border-box;
  }

  @media (max-width: 830px) {
    width: calc(100vw - 60px);
  }

  .trd-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .trd-title {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-right: 16px;
    font-size: 1rem;
    font-weight: 700;
  }

  /* SelectTrigger is a fixed 300px control — let the date hug its label. The
     DatePicker's own full-width wrapper is right-aligned for the same reason
     as in the list cells. */
  .trd-date button {
    width: auto;
  }

  .trd-date > div {
    text-align: right;
  }

  .trd-divider {
    margin: 20px 0 32px 0;
    border-bottom: 1px solid var(--border-primary);
    height: 1px;
  }

  .trd-body {
    display: flex;
    justify-content: space-between;
    position: relative;
  }

  .trd-form {
    width: 100%;
  }

  .trd-fields {
    display: flex;
    align-items: flex-end;
  }

  .trd-hours {
    width: 144px;
    margin-right: 16px;
    flex-shrink: 0;
  }

  .trd-hours .c-input-wrapper,
  .trd-hours input {
    width: 100%;
  }

  .trd-field {
    margin-right: 16px;
  }

  .trd-field:last-of-type {
    margin-right: 20px;
  }

  .trd-field .c-label {
    display: block;
    margin-bottom: 2px;
  }

  /* SelectTrigger defaults to 300px; the app's fields are 144px */
  .trd-field button {
    width: 144px;
  }

  .trd-billable {
    display: flex;
    align-items: center;
    margin-top: 24px;
  }

  .trd-billable .c-label {
    margin-left: 8px;
    cursor: pointer;
  }

  .trd-description-trigger {
    display: inline-flex;
    align-items: center;
    margin-top: 16px;
    color: var(--color-secondary);
    font-size: 14px;
    font-weight: 500;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: ${SANS};
  }

  .trd-description-trigger svg {
    margin-right: 4px;
    fill: var(--color-primary);
    transition: transform 200ms ease;
  }

  .trd-description-trigger.collapsed svg {
    transform: rotate(180deg);
  }

  .trd-textarea {
    width: 360px;
    max-width: 100%;
    margin-top: 8px;
    font-size: 12px;
    resize: none;
  }

  .trd-actions {
    display: flex;
    margin-top: 24px;
  }

  .trd-actions .c-button:first-child {
    margin-right: 12px;
  }

  /* the total sits bottom-right of the form column, like the app */
  .trd-total {
    position: absolute;
    right: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    align-self: flex-end;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  .trd-total svg {
    margin-right: 4px;
    fill: var(--color-primary);
    transition: transform 200ms ease;
  }

  .trd-total.collapsed svg {
    transform: rotate(180deg);
  }

  .trd-records {
    margin-top: 32px;
    border-top: 1px solid var(--border-primary);
  }

  .trd-record {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 36px;
    border-bottom: 1px solid var(--color-theme-300);
  }

  .trd-record-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
`;

const TimeRecordDialog = ({
  open,
  taskName,
  users,
  currentUserId,
  records,
  total,
  onSubmit,
  onClose,
}: {
  open: boolean;
  taskName: string;
  users: TimeCellUser[];
  currentUserId: string;
  records: TimeRecord[];
  total: number;
  onSubmit: (record: Omit<TimeRecord, "id">) => void;
  onClose: () => void;
}): ReactElement => {
  const [value, setValue] = useState("");
  const [date, setDate] = useState<number>(todayUnix());
  const [userId, setUserId] = useState<string>(currentUserId);
  const [jobTypeId, setJobTypeId] = useState<string>("devops");
  const [billable, setBillable] = useState(false);
  const [showDescription, setShowDescription] = useState(true);
  const [description, setDescription] = useState("");
  const [showRecords, setShowRecords] = useState(false);

  const user = users.find((item) => item.id === userId);
  const jobType = JOB_TYPES.find((item) => item.id === jobTypeId);

  /** "1:30" / "1.5" → decimal hours */
  const hours = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) {
      return 0;
    }
    if (trimmed.includes(":")) {
      const [h, m] = trimmed.split(":");
      return Number(h || 0) + Number(m || 0) / 60;
    }
    return Number(trimmed) || 0;
  }, [value]);

  const handleSubmit = useCallback(() => {
    onSubmit({
      date,
      userId,
      userName: user?.name ?? "",
      jobType: jobType?.name ?? "",
      hours,
      billable,
      description,
    });
  }, [billable, date, description, hours, jobType, onSubmit, user, userId]);

  return (
    <Modal open={open} onClose={onClose}>
      <SlideFromTop in={open}>
        <StyledTimeRecordPaper role="dialog" aria-label="Add time record">
          <div className="trd-header">
            <span className="trd-title">{taskName}</span>
            <span className="trd-date">
              <DatePicker
                mode="daily"
                required
                firstDayOfWeek={1}
                position="bottom-end"
                selected={{ from: date, to: date }}
                onSave={(range) =>
                  range?.from ? setDate(Number(range.from)) : undefined
                }
                target={
                  <SelectTrigger>{formatDateRange(date, date)}</SelectTrigger>
                }
              />
            </span>
          </div>

          <div className="trd-divider" />

          <div className="trd-body">
            <div className="trd-form">
              <div className="trd-fields">
                <span className="trd-hours">
                  <InputHours
                    autoFocus
                    allowEmptyValue
                    size="biggest"
                    placeholder="00:00"
                    value={value}
                    onChange={setValue}
                  />
                </span>

                <span className="trd-field">
                  <Label size="small">User</Label>
                  <Select
                    options={users}
                    selected={userId}
                    forceCloseMenu
                    onChange={(next) => setUserId(String(next))}
                    target={
                      <SelectTrigger id="trd-user">{user?.name}</SelectTrigger>
                    }
                  />
                </span>

                <span className="trd-field">
                  <Label size="small">Job Type</Label>
                  <Select
                    options={JOB_TYPES}
                    selected={jobTypeId}
                    forceCloseMenu
                    onChange={(next) => setJobTypeId(String(next))}
                    target={
                      <SelectTrigger id="trd-job-type">
                        {jobType?.name}
                      </SelectTrigger>
                    }
                  />
                </span>

                <span className="trd-billable">
                  <Checkbox
                    id="trd-billable"
                    checked={billable}
                    onChange={() => setBillable((prev) => !prev)}
                  />
                  <Label htmlFor="trd-billable" weight="regular">
                    Billable
                  </Label>
                </span>
              </div>

              <button
                type="button"
                className={
                  showDescription
                    ? "trd-description-trigger"
                    : "trd-description-trigger collapsed"
                }
                onClick={() => setShowDescription((prev) => !prev)}
              >
                <CollapseExpandSingleIcon />
                <span>Description</span>
              </button>

              {showDescription ? (
                <div>
                  <Textarea
                    className="trd-textarea"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </div>
              ) : null}

              <div className="trd-actions">
                <Button
                  variant="primary"
                  disabled={hours <= 0}
                  onClick={handleSubmit}
                >
                  Add Time Record
                </Button>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </div>

            {total > 0 ? (
              <button
                type="button"
                className={showRecords ? "trd-total" : "trd-total collapsed"}
                onClick={() => setShowRecords((prev) => !prev)}
              >
                <CollapseExpandSingleIcon />
                <Header3 color="theme-secondary" tabularNums>
                  Total: {formatHours(total, false, true)}h
                </Header3>
              </button>
            ) : null}
          </div>

          {showRecords && records.length > 0 ? (
            <div className="trd-records">
              {records.map((record) => (
                <div key={record.id} className="trd-record">
                  <span className="trd-record-left">
                    <Caption1 weight="bold" color="secondary">
                      {formatDateRange(record.date, record.date)}
                    </Caption1>
                    <Body2>{record.userName}</Body2>
                    <Caption1 color="secondary">{record.jobType}</Caption1>
                  </span>
                  <Body2 weight="bold" tabularNums>
                    {formatHours(record.hours)}h
                  </Body2>
                </div>
              ))}
            </div>
          ) : null}
        </StyledTimeRecordPaper>
      </SlideFromTop>
    </Modal>
  );
};
